<script lang="ts" setup>
import {onMounted, ref, watch, computed, inject, reactive} from "vue";
import {RouterLink, RouterView, useRoute, useRouter} from "vue-router";
import {Tooltip, Dropdown} from "bootstrap";
import {storeToRefs} from "pinia";
import {useQueryClient} from "@tanstack/vue-query";
import {useSessionStore} from "./stores/session-store";
import {logout} from "@/queries/auth";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import {useUserMemberships, useUpdateUserTheme} from "@/queries/users";
import {bindKey, bindKeyCombo} from "@rwh/keystrokes";
import Toast from "./views/shared/components/Toast.vue";
import {utilities} from "@/helpers/utilities";
import NavBar from "@/views/shared/components/NavBar.vue";
import NotificationBell from "@/components/notifications/NotificationBell.vue";
import type {User} from "@/types";
import {useSwitchOrganisation} from "@/queries/orgs";
import {useAlertStore} from "@/stores/alerts-store";
import {useBlockUI} from "@/helpers/useBlockUI";
import LoginView from "@/views/login/LoginView.vue";
import {applyDarkModeTokens} from "@/composables/useTheme";

const { switchOrganisationAsync } = useSwitchOrganisation();
const { updateUserTheme } = useUpdateUserTheme();
const queryClient = useQueryClient();
const sessionStore = useSessionStore();
const {session} = storeToRefs(sessionStore);

const route = useRoute();
const router = useRouter();
const is404 = ref(false);
// authResolved gates ALL rendering and queries until the server-side session check completes.
// Declared before membershipsQueryEnabled so the computed can reference it without TDZ error.
const authResolved = ref(false);

// Must also gate on authResolved — otherwise the query fires immediately with stale persisted
// isAuthenticated:true, gets a 401, and the fetch-wrapper redirect races clearSession().
const membershipsQueryEnabled = computed(() => authResolved.value && session.value.isAuthenticated);
const { memberships } = useUserMemberships(membershipsQueryEnabled);

const {blockUI, unblockUI} = useBlockUI();

onMounted(async () => {
  await router.isReady();
  await sessionStore.syncSession();
  authResolved.value = true;
  if (route.name == "not-found") {
    is404.value = true;
  }
});

onMounted(() => {
  new Tooltip(document.body, {
    selector: "[data-bs-toggle='tooltip']",
    trigger: "hover",
  });

  bindKeyCombo("control + d", (e) => {
    e.finalKeyEvent.preventDefault();
    toggleDark();
  });
  bindKeyCombo("control + f", (e) => {
    toggleFullscreen();
  });

  bindKeyCombo("control + k", (e) => {
    e.finalKeyEvent.preventDefault();
    toggleNav();
  });

  // Listen for offcanvas show/hide events
  const offcanvasElement = document.getElementById('sidebarMenu');
  if (offcanvasElement) {
    offcanvasElement.addEventListener('show.bs.offcanvas', () => {
      isOffcanvasOpen.value = true;
    });
    offcanvasElement.addEventListener('hide.bs.offcanvas', () => {
      isOffcanvasOpen.value = false;
    });
  }
});

const showFullscreen = ref(true);
const isFullscreenPage = computed(
    () => showFullscreen.value && route.fullPath.indexOf("builder") > 0
); // || (route.fullPath.indexOf('workflow2') > 0));
const getFullscreenClass = computed(() =>
    showFullscreen.value && route.fullPath.indexOf("builder") > 0
        ? " fullscreen"
        : ""
); // || (route.fullPath.indexOf('workflow2') > 0) ? 'fullscreen' : '');
const getFullscreenColumnClass = computed(() =>
    showFullscreen.value
        ? ""
        : route.fullPath.indexOf("builder")
            ? " firstpanel border "
            : ""
);

function toggleFullscreen() {
  showFullscreen.value = !showFullscreen.value;
}

const dateYear = computed(() => new Date().getFullYear());
const timeUTC = computed(() => {
  const hours = new Date().getUTCHours(),
      mins = new Date().getUTCMinutes();
  return (
      (hours > 9 ? hours : "0" + hours) + ":" + (mins > 9 ? mins : "0" + mins)
  );
});

const navNarrowClass = ref("");
const isMenuCollapsed = ref(false);
const isOffcanvasOpen = ref(false);
const menuCollapse = ref({} as HTMLElement | null);
const tooltips = ref([]);
const isDark = computed({
  get: () => session.value.user.settings.theme === "dark",
  set: (value: boolean) => {
    session.value.user.settings.theme = value ? "dark" : "light";
  },
});

// Computed: Menu should never be collapsed when offcanvas is open (mobile)
const effectiveMenuCollapsed = computed(() => {
  return isOffcanvasOpen.value ? false : isMenuCollapsed.value;
});

function toggleNav() {
  isMenuCollapsed.value = !isMenuCollapsed.value;
  if (isMenuCollapsed.value) {
    navNarrowClass.value = "narrow";
    utilities.disableTooltip(".sidebar");
  } else {
    utilities.enableTooltip(".sidebar");
    navNarrowClass.value = "";
  }
}

async function switchOrganization(orgId: string) {
  blockUI();
  const currentRoute = router.currentRoute.value;

  // Check if the current route has ANY resource-specific parameters (detail pages)
  // Common patterns: projectid, connectionid, productid, subscriptionId, scheduleId, id, etc.
  const hasResourceId = Object.keys(currentRoute.params).length > 0 &&
    Object.values(currentRoute.params).some(param => param && param !== 'new');

  try {
    const dropdownElement = document.querySelector(
        '[data-bs-toggle="dropdown"][aria-expanded="true"]'
    );
    if (dropdownElement) {
      const dropdown = Dropdown.getInstance(dropdownElement);
      if (dropdown) {
        dropdown.hide();
      }
    }

    await switchOrganisationAsync(orgId);
    await sessionStore.syncSession();

    // Every TanStack query in the app is implicitly scoped to "the current org" (the API uses the
    // session cookie's current org), but most query keys don't include orgId — so without this
    // they'd keep serving the prior org's cached data. invalidateQueries() marks every query stale
    // and refetches active observers (sub-nav projects, products, subscribed products, etc.).
    // We use invalidate rather than clear() because clear() destroys observers without refetching.
    await queryClient.invalidateQueries();

  useAlertStore().addInfo(
      "Switched Organisation",
      `Switched to ${session.value.currentOrgName}`
  );

    // Smart navigation: redirect to dashboard for resource-specific detail pages
    // The RouterView key change will automatically remount components for list pages
    if (hasResourceId) {
      // Detail pages with specific IDs - go to dashboard as the resource may not exist in the new org
  await router.push({name: "home"});
}
  } finally {
    unblockUI();
  }
}

const logoImagePath = computed(() => {
  return isDark.value
      ? "/images/Blackbox-Logo_Inverted.png"
      : "/images/Blackbox-Logo.png";
});

const logoImagePathLogoOnly = computed(() => {
  return isDark.value
      ? "/images/Blackbox-Logo_Inverted2.png"
      : "/images/Blackbox-Logo-only.png";
});

const membershipOptions = computed(() => {
  return memberships.value || [];
});

const initials = computed(() => {
  return session.value.user.initials;
});

const selectedOrgId = computed({
  get: () => session.value.currentOrgId,
  set: async (value: string) => {
    await switchOrganization(value);
  },
});

/**
 * Determines if the user can switch organizations.
 * Shows the switcher if the user has multiple organizations available to switch to.
 * This is determined by the memberships list, which already filters based on:
 * - SuperAdmins: see all orgs
 * - Partner org users: see their org + client orgs
 * - Regular users: see only their direct memberships
 */
const canSwitchOrganization = computed(() => {
  const hasMultipleOrgs = (memberships.value?.length ?? 0) > 1;
  return hasMultipleOrgs;
});

function logOff() {
  logout();
}

function toggleDark() {
  isDark.value = !isDark.value;
  updateUserTheme(isDark.value ? "dark" : "light");
}

// Function to close dropdown menus
function closeDropdownMenus() {
  // Find all open dropdown toggles and close them
  const openDropdowns = document.querySelectorAll(
      '[data-bs-toggle="dropdown"][aria-expanded="true"]'
  );
  openDropdowns.forEach((dropdown) => {
    const instance = Dropdown.getInstance(dropdown);
    if (instance) {
      instance.hide();
    }
  });
}

watch(
    () => session.value.user?.settings?.theme,
    (newTheme) => {
      document.documentElement.setAttribute("data-bs-theme", newTheme);
      // Re-derive the dark-mode brand tokens: they're set inline on <html> and must be recomputed
      // (or cleared for light) whenever the theme flips. See applyDarkModeTokens in useTheme.
      applyDarkModeTokens();
    },
    {immediate: true}
);

watch(
    () => route.name,
    (newRouteName) => {
      if (newRouteName === "builder" || newRouteName === "builder-project") {
        document.body.classList.add("builder-page");
      } else {
        document.body.classList.remove("builder-page");
      }
    },
    {immediate: true}
);
</script>

<template>
  <!-- Skeleton layout during bootstrap auth check -->
  <template v-if="!authResolved">
    <header class="topnav navbar flex-nowrap d-flex border-bottom placeholder-wave">
      <div class="d-flex align-items-center ps-3">
        <span class="placeholder skeleton-logo rounded"></span>
      </div>
      <ul class="navbar-nav navbar-nav-icons d-flex flex-row align-items-center me-3 gap-3">
        <li><span class="placeholder col-4 rounded"></span></li>
        <li><span class="placeholder skeleton-icon rounded-circle"></span></li>
        <li><span class="avatar placeholder"></span></li>
      </ul>
    </header>
    <div class="main d-flex">
      <nav class="sidebar navbar navbar-expand-lg pt-4 flex-column border-end col-lg-2">
        <div class="px-3 w-100">
          <SkeletonLoader type="panel" :lines="8" animation="wave" />
        </div>
      </nav>
      <div class="content flex-fill p-4">
        <SkeletonLoader type="header" animation="wave" />
        <SkeletonLoader type="table" :rows="5" animation="wave" class="mt-4" />
      </div>
    </div>
  </template>

  <!-- Real layout once auth is resolved -->
  <template v-else>

  <template v-if="route.meta.public">
    <RouterView />
  </template>

  <template v-else-if="session.isAuthenticated">
    <Toast></Toast>

    <!--TOP NAV BAR-->
    <header class="topnav navbar flex-nowrap d-flex border-bottom">
      <div class="d-flex align-items-start align-items-center">
        <ul class="navbar-nav flex-row d-lg-none">
          <li class="nav-item text-nowrap">
            <button
                class="nav-link px-3 pe-0"
                type="button"
                data-bs-toggle="offcanvas"
                data-bs-target="#sidebarMenu"
                aria-controls="sidebarMenu"
                aria-expanded="false"
                aria-label="Toggle navigation"
            >
              <span class="navbar-toggler-icon"></span>
            </button>
          </li>
        </ul>
        <a class="navbar-brand ps-3 py-0 me-0" href="/">
          <img
              height="60px"
              :src="logoImagePath"
              class="d-none d-xs-none d-sm-none d-md-block"
              alt="Blackbox Connector"
          />
          <img
              height="60px"
              :src="logoImagePathLogoOnly"
              class="d-sm-block d-xs-block d-md-none"
              alt="Blackbox Connector"
          />
        </a>
      </div>
      <ul
          class="navbar-nav navbar-nav-icons d-flex flex-row justify-content-lg-end align-items-center me-3"
      >
        <li class="nav-item d-flex">
          <div class="dropdown-center">
            <a
                class="nav-link dropdown-toggle"
                href="#"
                role="button"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                data-bs-auto-close="outside"
            >
              {{ session.currentOrgName }}
            </a>

            <div
                class="dropdown-menu dropdown-menu-end py-0 dropdown-profile shadow border"
            >
              <div class="card position-relative border-0">
                <!-- Org context header — links to the org overview; also anchors the dropdown when
                     there's nothing to switch -->
                <RouterLink
                    class="org-header text-center pt-4 pb-3 d-flex align-items-center flex-column text-decoration-none"
                    :to="{ name: 'org' }"
                    @click="closeDropdownMenus"
                >
                  <i class="bi bi-building fs-2 text-body-tertiary mb-2"></i>
                  <h6 class="h5 mb-0 text-body-emphasis">{{ session.currentOrgName }}</h6>
                </RouterLink>
                <hr class="dropdown-divider"/>

                <!-- Organization Switcher Section - Only shown to SuperAdmins and Partner-enabled orgs -->
                <template v-if="canSwitchOrganization">
                <div class="overflow-auto scrollbar">
                  <ul class="nav d-flex flex-column mb-2 pb-1">
                    <li class="nav-item">
                      <a class="nav-link px-3 d-block" href="#!">
                        <i class="bi bi-arrow-repeat"></i>Switch Organisation</a
                      >
                    </li>
                  </ul>
                </div>
                <div
                    class="text-center pb-3 d-flex align-items-center flex-column"
                >
                  <div class="navbar-switch-org">
                    <select
                        class="form-select form-select-sm"
                        v-model="selectedOrgId"
                    >
                      <option
                          v-for="membership in membershipOptions"
                          :key="membership.orgId"
                          :value="membership.orgId"
                          :selected="membership.orgId === selectedOrgId"
                      >
                        {{ membership.orgName }}
                      </option>
                    </select>
                  </div>
                </div>
                <hr class="dropdown-divider"/>
                </template>

                <!-- Settings and other options - Always shown -->
                <div class="overflow-auto scrollbar">
                  <ul class="nav d-flex flex-column mb-2 pb-1">
                    <li class="nav-item">
                      <RouterLink
                          class="nav-link px-3 d-block"
                          :to="{ name: 'settings' }"
                          @click="closeDropdownMenus"
                      >
                        <i class="bi bi-gear"></i>Settings</RouterLink
                      >
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </li>
        <NotificationBell />
        <li
            class="nav-item dropdown d-flex"
            style="position: relative; z-index: 1030"
        >
          <a
              class="nav-link lh-1 pe-0"
              id="navbarDropdownUser"
              href="#!"
              role="button"
              data-bs-toggle="dropdown"
              data-bs-auto-close="outside"
              aria-haspopup="true"
              aria-expanded="false"
          >
            <div class="avatar avatar-l d-flex align-items-center justify-content-center">
              <span :data-letters="initials"></span>
            </div>
          </a>
          <div
              class="dropdown-menu dropdown-menu-end navbar-dropdown-caret py-0 dropdown-profile shadow border"
              aria-labelledby="navbarDropdownUser"
          >
            <div class="card position-relative border-0">
              <div class="card-body p-0">
                <div
                    class="text-center pt-4 pb-3 d-flex align-items-center flex-column"
                >
                  <div class="avatar avatar-xl d-flex align-items-center justify-content-center">
                    <span :data-letters="initials"></span>
                  </div>
                  <h6 class="h5 mt-2 mb-0 text-body-emphasis">
                    {{ session.user.firstName }} {{ session.user.lastName }}
                  </h6>
                </div>
              </div>
              <div class="overflow-auto scrollbar">
                <ul class="nav d-flex flex-column mb-2 pb-1">
                  <li class="nav-item">
                    <RouterLink
                        class="nav-link px-3 d-block"
                        :to="{ name: 'profile' }"
                        @click="closeDropdownMenus"
                    >
                      <i class="bi bi-person"></i>Profile
                    </RouterLink
                    >
                  </li>
                  <!--                  <li class="nav-item"><a class="nav-link px-3 d-block" href="#!">-->
                  <!--                    <i class="bi bi-arrow-repeat"></i>Switch Org</a>-->
                  <!--                  </li>-->
                </ul>
              </div>
              <div>
                <ul class="nav d-flex flex-column mb-2 pb-1">
                  <li class="nav-item">
                    <a
                        class="nav-link px-3 d-block"
                        href="#!"
                        @click="closeDropdownMenus"
                    >
                      <div class="form-check form-switch">
                        <input
                            class="form-check-input"
                            v-on:click="toggleDark"
                            type="checkbox"
                            v-model="isDark"
                            role="switch"
                            id="toggleDarkMode"
                        />
                        <label class="form-check-label" for="toggleDarkMode"
                        >Dark mode</label
                        >
                      </div>
                    </a>
                  </li>
                </ul>
              </div>
              <div
                  class="card-footer p-0 pt-3 pb-3 border-top border-translucent"
              >
                <div class="px-3 d-flex align-items-center">
                  <a
                      class="btn btn-secondary w-100"
                      href="#!"
                      @click="logOff"
                  >
                    <i class="bi bi-box-arrow-right pe-1"></i>
                    Sign out
                  </a>
                </div>
              </div>
            </div>
          </div>
        </li>
        <!--        <li class="nav-item dropdown d-flex">-->
        <!--          <a class="nav-link" id="navbarDropdownNindeDots" href="#" role="button" data-bs-toggle="dropdown" aria-haspopup="true" data-bs-auto-close="outside" aria-expanded="false">-->
        <!--            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">-->
        <!--              <circle cx="2" cy="2" r="2" fill="currentColor"></circle>-->
        <!--              <circle cx="2" cy="8" r="2" fill="currentColor"></circle>-->
        <!--              <circle cx="2" cy="14" r="2" fill="currentColor"></circle>-->
        <!--              <circle cx="8" cy="8" r="2" fill="currentColor"></circle>-->
        <!--              <circle cx="8" cy="14" r="2" fill="currentColor"></circle>-->
        <!--              <circle cx="14" cy="8" r="2" fill="currentColor"></circle>-->
        <!--              <circle cx="14" cy="14" r="2" fill="currentColor"></circle>-->
        <!--              <circle cx="8" cy="2" r="2" fill="currentColor"></circle>-->
        <!--              <circle cx="14" cy="2" r="2" fill="currentColor"></circle>-->
        <!--            </svg>-->
        <!--          </a>-->
        <!--          <div class="dropdown-menu dropdown-menu-end navbar-dropdown-caret py-0 dropdown-nine-dots shadow border" aria-labelledby="navbarDropdownNindeDots">-->
        <!--            <div class="card bg-body-emphasis position-relative border-0">-->
        <!--              <div class="card-body pt-3 px-3 pb-0 overflow-auto scrollbar" style="height: 20rem;">-->
        <!--                <div class="row text-center align-items-center gx-0 gy-0">-->
        <!--                  <div class="col-4">-->
        <!--                    <a class="d-block bg-body-secondary-hover p-2 rounded-3 text-center text-decoration-none mb-3" href="#!"><img src="assets/img/nav-icons/behance.webp" alt="" width="30">-->
        <!--                      <p class="mb-0 text-body-emphasis text-truncate fs-10 mt-1 pt-1">Behance</p>-->
        <!--                    </a></div>-->
        <!--                  <div class="col-4">-->
        <!--                    <a class="d-block bg-body-secondary-hover p-2 rounded-3 text-center text-decoration-none mb-3" href="#!"><img src="assets/img/nav-icons/google-cloud.webp" alt="" width="30">-->
        <!--                      <p class="mb-0 text-body-emphasis text-truncate fs-10 mt-1 pt-1">Cloud</p>-->
        <!--                    </a></div>-->
        <!--                -->
        <!--                </div>-->
        <!--              </div>-->
        <!--            </div>-->
        <!--          </div>-->
        <!--        </li>-->
      </ul>
    </header>

    <!--MAIN AREA: SIDE BAR + CONTENT-->
    <div class="main d-flex">
      <!--SIDE BAR-->
      <nav class="sidebar navbar navbar-expand-lg pt-4 flex-column border-end col-lg-2" v-bind:class="navNarrowClass">
        <div class="px-0 container-fluid">
          <div class="offcanvas-lg offcanvas-end d-flex flex-fill" tabindex="-1" id="sidebarMenu" aria-labelledby="offcanvasNavbarLabel">
            <div class="offcanvas-body flex-fill d-flex flex-column">
              <nav-bar :is-menu-collapsed="effectiveMenuCollapsed"></nav-bar>
              <!-- Collapse button - sticky to bottom when scrolling -->
              <div
                  v-bind:class="navNarrowClass"
                  class="menuCollapse d-none d-lg-block mt-auto"
                  :class="{ 'px-3': !isMenuCollapsed, 'text-center': isMenuCollapsed }"
              >
                <a
                    @click="toggleNav"
                    data-bs-toggle="tooltip"
                    title="Toggle Menu Collapse (ctrl-k)"
                    class="btn btn-sm border-0 p-0"
                    :class="isMenuCollapsed ? '' : 'w-100'"
                >
                  <i
                      :class="isMenuCollapsed ? 'bi-arrow-bar-right' : 'bi-arrow-bar-left'"
                      class="bi"
                      :style="{ marginRight: isMenuCollapsed ? '0' : '0.5rem' }"
                      data-cy="is-menu-collapsed"
                  ></i>
                  <span v-if="!isMenuCollapsed">Collapse View</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>
      <!--CONTENT-->
      <!-- min-w-0: let the flex-fill content shrink to the available width so a wide
           descendant (e.g. the run-history view) can't blow the column past the viewport. -->
      <div class="content flex-fill min-w-0">
        <RouterView
          :key="session.currentOrgId"
            @toggleFullscreen="toggleFullscreen"
            @toggleDark="toggleDark"
        ></RouterView>
      </div>
    </div>
    <!--FOOTER-->
<!--    <template v-if="!isFullscreenPage">-->
    <template v-if="false">
      <div class="footernav">

        <div class="container">
          <footer class="d-flex flex-wrap justify-content-between align-items-center py-3 my-4">
            
            <span class="mb-3 mb-md-0 text-body-secondary">© {{ dateYear }} - Full Sail Partners</span>
            <span class=" col-md-4 justify-content-end  text-body-secondary d-flex">
              {{ timeUTC }} UTC
            </span>
<!--            <ul class="nav col-md-4 justify-content-end list-unstyled d-flex">-->
<!--              <li class="ms-3"><a class="text-body-secondary" href="#" aria-label="Instagram">-->
<!--                <svg class="bi" width="24" height="24" aria-hidden="true">-->
<!--                  <use xlink:href="#instagram"></use>-->
<!--                </svg>-->
<!--              </a></li>-->
<!--              <li class="ms-3"><a class="text-body-secondary" href="#" aria-label="Facebook">-->
<!--               asd-->
<!--              </a></li>-->
<!--            </ul>-->
          </footer>
        </div>

<!--        <div class="container">-->
<!--          <footer class="py-3">-->
<!--            <p class="text-center text-body-secondary">-->
<!--              <span>© {{ dateYear }} - Full Sail Partners </span>-->
<!--              <span class="text-end">{{ timeUTC }} UTC</span>-->
<!--            </p>-->
<!--          </footer>-->
<!--        </div>-->
      </div>
    </template>
  </template>

  <template v-else>
    <LoginView />
  </template>

  </template><!-- end v-else (authResolved) -->
</template>

<!--global styles-->
<style>
html,
body {
  overflow-x: hidden !important;
}

body {
  background: var(--bs-tertiary-bg) url(/images/watermark.png) bottom right no-repeat !important;
}
</style>

<!--page styles-->
<style lang="scss" scoped>
// Import Bootstrap mixins for responsive breakpoints
@import 'bootstrap/scss/functions';
@import 'bootstrap/scss/variables';
@import 'bootstrap/scss/mixins';

// Layout custom properties
$topnav-height: 3.5rem;        // top navbar height
$sidebar-width: 16.666%;       // col-lg-2 equivalent
$sidebar-width-narrow: 4.5rem; // collapsed width for icons
$menu-collapse-height: 3rem;   // fixed collapse button at sidebar bottom

// Styles for the top navigation bar
.topnav {

  background-color: var(--bs-body-bg);

  @include media-breakpoint-down(sm) {
    .navbar-nav-icons .dropdown-profile {
      min-width: 15.3125rem;
    }
  }

  @include media-breakpoint-down(lg) {
    border-bottom: 0 !important;
  }

  .navbar-nav-icons .dropdown-menu {
    position: absolute;
  }

  .navbar-nav-icons .dropdown-profile {
    min-width: 18.3125rem;
  }


  .navbar-nav-icons .dropdown-profile {
    font-size: 16px;
  }

  // Clickable org-name header — subtle hover so it reads as a link to the org overview
  .org-header {
    cursor: pointer;
    transition: background-color 0.15s ease-in-out;

    &:hover {
      background-color: var(--bs-tertiary-bg);
    }
  }

  .navbar .navbar-nav-icons .dropdown-menu.navbar-dropdown-caret:after {
    content: "";
    position: absolute;
    z-index: -1;
    width: 1.5rem;
    height: 1.5rem;
    top: -10px;
    left: 1.5rem;
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
    background: inherit;
    border-top-left-radius: 0.25rem;
    border-width: 1px 0 0 1px;
    border-style: solid;
    border-color: var(--bs-border-color);
    -webkit-box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  }

  .navbar .navbar-nav-icons .dropdown-menu.dropdown-menu-end.navbar-dropdown-caret {
    left: auto;
    right: -0.8625rem;
  }

  .navbar .navbar-nav-icons .dropdown-menu.dropdown-menu-end.navbar-dropdown-caret:after {
    right: 1.2rem;
    left: auto;
  }

  .navbar .navbar-nav-icons .dropdown-menu {
    margin-top: 1em;
  }

  .navbar-nav-icons .dropdown-profile .nav-link i {
    padding-right: 5px;
  }


  //https://ianwaldron.com/article/48/default-avatars-with-user-initials/
  .avatar.avatar-xl {
    height: 4rem;
    width: 4rem;
  }

  .avatar.avatar-xl > span[data-letters]::before {
    content: attr(data-letters);
    font-size: 1.5rem;
  }

  .avatar {
    /* button */
    border: none;
    font-family: inherit;
    cursor: pointer;
    outline: inherit;

    height: 2.5rem;
    width: 2.5rem;
    border-radius: 50%;
    color: #fff;
  }

  /* initials */
  .avatar > span[data-letters]::before {
    content: attr(data-letters);
    font-size: 1rem;
  }

  /* background gradients */

  .avatar:nth-child(1) {
    background: linear-gradient(to right top, #4b31a1, #b32993);
  }

  #navbarDropdownUser {
    padding: 0 1rem;
  }

}

// Main layout container - ensure sidebar fills viewport height
.main {
  min-height: calc(100vh - $topnav-height);
}

// Styles for the sidebar navigation
.sidebar {
  //min-height: 92vh;
  background-color: var(--bs-body-bg); //for some reason, the offcanvas is transparent without this
  overflow: hidden; // prevent nav-link hover backgrounds from overflowing during resize
  // Clearance for the position:fixed .menuCollapse button so the bottom-most nav item
  // isn't hidden underneath it when the sidebar is taller than the viewport.
  @include media-breakpoint-up(lg) {
    padding-bottom: $menu-collapse-height;
  }
  @include media-breakpoint-down(md) {
    border-right: 0 !important;
  }
}

// Styles for the sidebar in narrow (collapsed) mode
// See NavItem for other styles
.narrow {
  @include media-breakpoint-up(lg) {
    width: $sidebar-width-narrow;
  }
}
//the menu collapse button - fixed to viewport bottom
.menuCollapse {
  z-index: 5;

  border: 1px solid var(--bs-border-color);
  border-left: none;
  padding-top: 0.75rem;
  background-color: var(--bs-body-bg);
  position: fixed;
  height: $menu-collapse-height;
  bottom: 0;
  left: 0;
  width: $sidebar-width;
  align-items: center;

  // Collapsed/narrow state
  &.narrow {
    width: $sidebar-width-narrow;

    i {
      font-size: 1rem; // larger icon in narrow mode
    }
  }
}

.footernav {
  //background-color: var(--bs-body-bg);
  border-top: 1px solid var(--bs-border-color);
}

// Skeleton placeholders — shown during bootstrap auth check (~100-200ms)
.skeleton-logo {
  height: $topnav-height * 0.65;
  width: 8rem;
  display: inline-block;
}

.skeleton-icon {
  height: 1.75rem;
  width: 1.75rem;
  display: inline-block;
}
</style>
